import type { PeriodWarmJob } from "@/lib/api/periodWarm";
import { fetchPeriodWarmStatus, startPeriodWarm } from "@/lib/api/periodWarm";

export type PeriodWarmProgressState = {
  active: boolean;
  progress: number;
  label: string;
  job: PeriodWarmJob | null;
  error: string | null;
};

const IDLE: PeriodWarmProgressState = {
  active: false,
  progress: 0,
  label: "",
  job: null,
  error: null,
};

const statesByAccount = new Map<number, PeriodWarmProgressState>();
const pendingStarts = new Set<number>();
/** Comptes dont le job est terminé — poll allégé (1 fois / 30 s). */
const terminalPollAccounts = new Set<number>();
const listeners = new Set<() => void>();

const POLL_MS = 2500;
const TERMINAL_POLL_MS = 30_000;
let pollAccountId: number | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollRefCount = 0;
let pollFetcher: ((accountId: number) => Promise<void>) | null = null;
let pollIntervalMs = POLL_MS;
/** Empêche les polls qui se chevauchent (nudge / status longs). */
let pollInFlight = false;

function emit(): void {
  listeners.forEach((cb) => cb());
}

export function getPeriodWarmProgress(accountId?: number): PeriodWarmProgressState {
  if (accountId == null) {
    return IDLE;
  }
  return statesByAccount.get(accountId) ?? IDLE;
}

export function subscribePeriodWarmProgress(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function desiredPollIntervalMs(accountId: number): number {
  return terminalPollAccounts.has(accountId) ? TERMINAL_POLL_MS : POLL_MS;
}

function restartPollTimer(): void {
  if (pollTimer != null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (pollAccountId == null || pollFetcher == null) {
    return;
  }
  pollTimer = setInterval(() => {
    if (pollInFlight) return;
    if (pollAccountId != null && pollFetcher) {
      void pollFetcher(pollAccountId);
    }
  }, pollIntervalMs);
}

/** Relance le poll rapide — démarre le poll si besoin (ex. lancement depuis la modale). */
export function kickPeriodWarmPolling(
  accountId: number,
  fetcher?: (accountId: number) => Promise<void>
): void {
  terminalPollAccounts.delete(accountId);
  pollIntervalMs = POLL_MS;

  if (fetcher) {
    pollFetcher = fetcher;
  }

  if (pollFetcher == null) {
    return;
  }

  if (pollAccountId !== accountId || pollTimer == null) {
    if (pollTimer != null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    pollAccountId = accountId;
    pollIntervalMs = POLL_MS;
    void pollFetcher(accountId);
    restartPollTimer();
    return;
  }

  restartPollTimer();
  void pollFetcher(accountId);
}

/** Un seul poll global — évite les courses entre sidebar / workspace / modale. */
export function startPeriodWarmPolling(
  accountId: number,
  fetcher: (accountId: number) => Promise<void>
): void {
  pollRefCount += 1;
  pollFetcher = fetcher;

  if (pollAccountId === accountId && pollTimer != null) {
    const nextIntervalMs = desiredPollIntervalMs(accountId);
    if (nextIntervalMs !== pollIntervalMs) {
      pollIntervalMs = nextIntervalMs;
      restartPollTimer();
    }
    void fetcher(accountId);
    return;
  }

  if (pollTimer != null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  pollAccountId = accountId;
  pollIntervalMs = desiredPollIntervalMs(accountId);
  void fetcher(accountId);
  restartPollTimer();
}

export function stopPeriodWarmPolling(_accountId: number): void {
  pollRefCount = Math.max(0, pollRefCount - 1);
  if (pollRefCount > 0) {
    return;
  }
  if (pollTimer != null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  pollAccountId = null;
  pollFetcher = null;
}

export async function pollPeriodWarmStatus(accountId: number): Promise<void> {
  if (pollInFlight) return;
  pollInFlight = true;
  try {
    const res = await fetchPeriodWarmStatus(accountId);
    if (!res.ok) {
      return;
    }
    if (res.data.job != null) {
      applyPeriodWarmJob(accountId, res.data.job);
      return;
    }
    applyPeriodWarmJob(accountId, null);
  } finally {
    pollInFlight = false;
  }
}

export function setPeriodWarmStarting(accountId: number): void {
  pendingStarts.add(accountId);
  terminalPollAccounts.delete(accountId);
  statesByAccount.set(accountId, {
    active: true,
    progress: 0,
    label: "Préparation en cours…",
    job: null,
    error: null,
  });
  kickPeriodWarmPolling(accountId, pollPeriodWarmStatus);
  emit();
}

export function setPeriodWarmStartError(accountId: number, message: string): void {
  pendingStarts.delete(accountId);
  statesByAccount.set(accountId, {
    active: false,
    progress: 0,
    label: "",
    job: null,
    error: message,
  });
  emit();
}

function formatPeriodDays(days: number): string {
  if (days === 7) return "1 semaine";
  if (days === 30) return "1 mois";
  return "2 semaines";
}

function stateFromJob(job: PeriodWarmJob): PeriodWarmProgressState {
  if (job.status === "completed") {
    const done = job.bodiesDone;
    const total = job.totalMessages;
    const pct = total > 0 ? Math.round((done / total) * 100) : 100;
    return {
      active: false,
      progress: Math.min(100, pct),
      label:
        done >= total
          ? `Prêt · ${formatPeriodDays(job.periodDays)} · ${done}/${total} mails`
          : `Terminé · ${formatPeriodDays(job.periodDays)} · ${done}/${total} mails`,
      job,
      error: null,
    };
  }

  const done = job.bodiesDone;
  const folder = job.currentFolder ?? "…";

  return {
    active: job.status === "running" || job.status === "paused",
    progress: job.progress,
    label: `Lecture instantanée · ${done}/${job.totalMessages} · ${folder}`,
    job,
    error: null,
  };
}

export function applyPeriodWarmJob(
  accountId: number,
  job: PeriodWarmJob | null,
  options?: { force?: boolean }
): void {
  if (job === null || job.status === "cancelled") {
    if (!options?.force && pendingStarts.has(accountId)) {
      return;
    }
    pendingStarts.delete(accountId);
    statesByAccount.delete(accountId);
    terminalPollAccounts.add(accountId);
    emit();
    return;
  }

  const prev = statesByAccount.get(accountId);
  pendingStarts.delete(accountId);

  if (job.status === "completed") {
    terminalPollAccounts.add(accountId);
  } else {
    terminalPollAccounts.delete(accountId);
    kickPeriodWarmPolling(accountId);
  }

  const next = stateFromJob(job);

  if (
    prev?.job?.updatedAt === next.job?.updatedAt &&
    prev?.active === next.active &&
    prev?.progress === next.progress &&
    prev?.label === next.label
  ) {
    return;
  }

  statesByAccount.set(accountId, next);
  emit();
}

/** Arrêt forcé (purge cache, annulation) — ignore pendingStarts et le poll. */
export function resetPeriodWarmProgress(accountId: number): void {
  applyPeriodWarmJob(accountId, null, { force: true });
}

/** Lance la préparation — continue après fermeture de la modale. */
export async function launchPeriodWarm(
  accountId: number,
  periodDays: 7 | 14 | 30,
  folders: string[],
  totalMessages: number
): Promise<void> {
  const res = await startPeriodWarm(accountId, periodDays, folders, totalMessages);
  if (!res.ok) {
    pendingStarts.delete(accountId);
    setPeriodWarmStartError(accountId, res.message);
    return;
  }
  if (res.data.job == null) {
    pendingStarts.delete(accountId);
    setPeriodWarmStartError(accountId, "Impossible de démarrer la préparation.");
    return;
  }
  applyPeriodWarmJob(accountId, res.data.job);
  kickPeriodWarmPolling(accountId, pollPeriodWarmStatus);
}
